import { useMemo, type ReactNode } from 'react'
import { mergeAttributes } from '@tiptap/core'
import { Link } from '@tiptap/extension-link'
import { isAttachmentUrl } from '@highlight/common/product'
import rehypeRaw from 'rehype-raw'
import rehypeSanitize from 'rehype-sanitize'
import { Markdown, type MarkdownProps } from '@highlight/ui'

import { preserveAttachmentUrls } from './attachment-url'
import { ContentSlotContext } from './diff-marker'
import { AddedContent, RemovedContent } from './diff-marker-extension'
import { CalloutBlockquote, QuoteHeader } from './quote-header-extension'
import {
  hyphenateMarkerTags,
  rehypeQuoteSource,
  stagedActionComponents,
  stagedActionSanitizeSchema,
} from './staged-action-read-only'
import { useStagedActionImageRenderer, type StagedActionAttachment } from './staged-action-image'

export interface StagedActionMarkdownProps extends Omit<
  MarkdownProps,
  'renderImage' | 'components' | 'urlTransform' | 'remarkPlugins' | 'rehypePlugins'
> {
  attachments?: Array<StagedActionAttachment>
  /** Node rendered inside any `<content>` markers in `value`, replacing the marker's inner text (a preview or an editor). */
  contentSlot?: ReactNode
}

// Link extension that renders attachment: hrefs as inline spans instead of
// anchor tags, preventing broken navigation for attachment pseudo-URLs.
const attachmentAwareLinkExtension = Link.configure({
  openOnClick: false,
  autolink: true,
  linkOnPaste: true,
  HTMLAttributes: { rel: 'noopener noreferrer nofollow', target: '_blank' },
}).extend({
  renderHTML({ HTMLAttributes }) {
    const href = HTMLAttributes.href as string | undefined
    if (isAttachmentUrl(href)) {
      const rest = { ...HTMLAttributes }
      delete rest.href
      return ['span', rest, 0]
    }
    return ['a', mergeAttributes(this.options.HTMLAttributes, HTMLAttributes), 0]
  },
})

/**
 * Markdown for a staged action's content. Read-only by default, which covers every preview and
 * summary; `editable` is for the draft body the user edits. Both draw the action-specific pieces
 * (diff regions, quote headers, callout quotes, attachment links and images) the same way.
 */
export function StagedActionMarkdown({
  attachments,
  features,
  extensions,
  contentSlot,
  editable = false,
  children,
  value,
  defaultValue,
  ...props
}: StagedActionMarkdownProps) {
  const renderImage = useStagedActionImageRenderer(attachments)
  // Only swap in the attachment-aware link extension when links are enabled.
  // Keeping links on (rather than forcing them off) preserves tokenization for
  // normal `[text](url)` markdown; the custom extension just renders
  // `attachment:` hrefs as spans instead of broken anchors. When links are
  // disabled there are no link tokens to render, so the extension is dropped.
  const linkEnabled = features?.link !== false
  // Read-only, rehype-raw parses the `<added_content>`-style tags and rehype-sanitize drops any other
  // raw HTML the model wrote; the quote-header plugin runs after it and needs the (hyphenated)
  // source to find each header's quote.
  const readOnlySource = useMemo(
    () => (editable ? '' : hyphenateMarkerTags(children ?? value ?? defaultValue ?? '')),
    [editable, children, value, defaultValue],
  )
  const rehypePlugins = useMemo<MarkdownProps['rehypePlugins']>(
    () =>
      editable
        ? undefined
        : [rehypeRaw, [rehypeSanitize, stagedActionSanitizeSchema], rehypeQuoteSource(readOnlySource)],
    [editable, readOnlySource],
  )

  const mergedExtensions = useMemo(() => {
    const result: Array<NonNullable<typeof extensions>[number]> = [
      AddedContent,
      RemovedContent,
      QuoteHeader,
      CalloutBlockquote,
    ]
    if (linkEnabled) result.push(attachmentAwareLinkExtension)
    if (extensions) result.push(...extensions)
    return result
  }, [extensions, linkEnabled])

  return (
    <ContentSlotContext.Provider value={contentSlot ?? null}>
      <Markdown
        {...props}
        editable={editable}
        value={editable ? (children ?? value) : undefined}
        defaultValue={editable ? defaultValue : undefined}
        features={features}
        extensions={mergedExtensions}
        renderImage={renderImage}
        components={stagedActionComponents}
        rehypePlugins={rehypePlugins}
        urlTransform={preserveAttachmentUrls}
      >
        {editable ? undefined : readOnlySource}
      </Markdown>
    </ContentSlotContext.Provider>
  )
}
